import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
} from '@gapartners/ui';
import CloseIcon from '@mui/icons-material/Close';
import { SelectChangeEvent } from '@mui/material';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { SelectField } from '@gapartners/ui/select';
import { api } from '@/store/api';
import type { RootState } from '@/store';
import {
  useGetCallQualityMetricsQuery,
  useGetAgentQualityMetricsQuery,
} from '@/store/api/qualityMetrics';
import {
  CallQualityCard,
  SentimentChart,
  TalkTimeChart,
  SentimentTrend,
} from '@/components/quality-metrics';
import { getEnv } from '../../utils/env';

const CALLS_PER_PAGE = 50;

type SearchMode = null | 'agent' | 'call';

export function QualityMetricsDashboardPage() {
  const { t } = useTranslation();
  const currentUserId = useSelector((state: RootState) => state.auth.user?.id);
  const [searchMode, setSearchMode] = useState<SearchMode>(null);
  const [agentId, setAgentId] = useState('');
  const [callId, setCallId] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [startTime, setStartTime] = useState<string>(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [endTime, setEndTime] = useState<string>(
    new Date().toISOString().split('T')[0]
  );

  const { data: directReportsData = [], isLoading: isLoadingDirectReports } =
    api.useGetDirectReportsQuery();

  const directReports = useMemo(() => {
    return directReportsData.filter(agent => agent.id !== currentUserId);
  }, [directReportsData, currentUserId]);

  const directReportOptions = useMemo(() => {
    const useDummyData = getEnv('USE_DUMMY_DATA') === 'true';
    const source = useDummyData
      ? [
          ...directReports,
          {
            id: 'd9e931e0-51ee-4456-9bec-ad4e59d72d65',
            displayName: 'Mock Mykyta',
          },
          {
            id: 'd70b3e95-a1a9-4bdc-a576-e15374c17cfd',
            displayName: 'Mock Parikshit',
          },
          {
            id: '96f1782d-1567-4d9a-a4be-3c151779cc1d',
            displayName: 'Mock Manisha',
          },
        ]
      : directReports;
    return source.map(agent => ({
      value: agent.id,
      label: agent.displayName,
    }));
  }, [directReports]);

  // Call history dropdown only (same endpoint as manager Call History tab).
  const teamAgentIdsForCallHistory = useMemo(
    () => directReportOptions.map(option => option.value),
    [directReportOptions]
  );

  const {
    data: callHistoryResponse,
    isLoading: isLoadingCallHistory,
    isFetching: isFetchingCallHistory,
  } = api.useGetCallsByAgentManagerQuery(
    {
      agentIds: teamAgentIdsForCallHistory,
      limit: CALLS_PER_PAGE,
    },
    {
      skip: teamAgentIdsForCallHistory.length === 0,
      refetchOnMountOrArgChange: true,
    }
  );

  const callOptions = useMemo(() => {
    return (callHistoryResponse?.calls ?? []).map(call => {
      const when = new Date(call.startDate).toLocaleString([], {
        dateStyle: 'short',
        timeStyle: 'short',
      });
      const combinedAgentName = call?.pickedBy
        .map(pickedBy => pickedBy.agentName)
        .join(', ');
      const clientName = call.clientDetails?.name?.trim();
      const typeLabel = call.type === 'chat' ? '💬 Chat' : '☎️ Call';
      const parts = [
        typeLabel,
        when,
        call.phoneNumber,
        clientName,
        combinedAgentName,
      ].filter(Boolean);
      return { value: call.contactId, label: parts.join(' · ') };
    });
  }, [callHistoryResponse?.calls]);

  const {
    data: callData,
    isLoading: callLoading,
    error: callError,
  } = useGetCallQualityMetricsQuery({ contactId: callId }, { skip: !callId });

  const {
    data: agentData,
    isLoading: agentLoading,
    error: agentError,
  } = useGetAgentQualityMetricsQuery(
    {
      agentId,
      startTime: startTime ? new Date(startTime).toISOString() : undefined,
      endTime: endTime ? new Date(endTime).toISOString() : undefined,
    },
    { skip: !agentId }
  );

  const handleDateChange = (type: 'start' | 'end', value: string) => {
    if (type === 'start') {
      setStartTime(value);
    } else {
      setEndTime(value);
    }
  };

  const handleAgentSearch = () => {
    if (agentId.trim()) {
      setIsSearching(true);
      setSearchMode('agent');
      setTimeout(() => setIsSearching(false), 500);
    }
  };

  const handleCallSearch = () => {
    if (callId.trim()) {
      setIsSearching(true);
      setSearchMode('call');
      setTimeout(() => setIsSearching(false), 500);
    }
  };

  const handleClose = () => {
    setSearchMode(null);
    setAgentId('');
    setCallId('');
  };

  const isLoadingCallOptions = isLoadingCallHistory || isFetchingCallHistory;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          {t('pages.manager.qualityMetrics.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('pages.manager.qualityMetrics.description')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          mb: 4,
        }}
      >
        {/* Agent Quality Search Card */}
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            minHeight: 350,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('pages.manager.qualityMetrics.agent.cardTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('pages.manager.qualityMetrics.agent.cardDescription')}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              mb: 3,
              flexGrow: 1,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
            >
              {t('pages.manager.qualityMetrics.agent.availableMetrics')}
            </Typography>
            <Typography variant="body2" sx={{ pl: 1 }}>
              {t('pages.manager.qualityMetrics.agent.metric1')}
            </Typography>
            <Typography variant="body2" sx={{ pl: 1 }}>
              {t('pages.manager.qualityMetrics.agent.metric2')}
            </Typography>
            <Typography variant="body2" sx={{ pl: 1 }}>
              {t('pages.manager.qualityMetrics.agent.metric3')}
            </Typography>
            <Typography variant="body2" sx={{ pl: 1 }}>
              {t('pages.manager.qualityMetrics.agent.metric4')}
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              setSearchMode(searchMode === 'agent' ? null : 'agent')
            }
            fullWidth
          >
            {searchMode === 'agent'
              ? t('pages.manager.qualityMetrics.agent.hideButton')
              : t('pages.manager.qualityMetrics.agent.searchButton')}
          </Button>
        </Paper>

        {/* Call Quality Search Card */}
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            minHeight: 350,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('pages.manager.qualityMetrics.call.cardTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('pages.manager.qualityMetrics.call.cardDescription')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              mb: 3,
              flexGrow: 1,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
            >
              {t('pages.manager.qualityMetrics.call.availableMetrics')}
            </Typography>
            <Typography variant="body2" sx={{ pl: 1 }}>
              {t('pages.manager.qualityMetrics.call.metric1')}
            </Typography>
            <Typography variant="body2" sx={{ pl: 1 }}>
              {t('pages.manager.qualityMetrics.call.metric2')}
            </Typography>
            <Typography variant="body2" sx={{ pl: 1 }}>
              {t('pages.manager.qualityMetrics.call.metric3')}
            </Typography>
            <Typography variant="body2" sx={{ pl: 1 }}>
              {t('pages.manager.qualityMetrics.call.metric4')}
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={() => setSearchMode(searchMode === 'call' ? null : 'call')}
            fullWidth
          >
            {searchMode === 'call'
              ? t('pages.manager.qualityMetrics.call.hideButton')
              : t('pages.manager.qualityMetrics.call.searchButton')}
          </Button>
        </Paper>
      </Box>

      {/* Agent Search Form & Results */}
      {searchMode === 'agent' && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 4,
            bgcolor: 'action.hover',
            border: '2px solid',
            borderColor: 'primary.main',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              {t('pages.manager.qualityMetrics.agent.searchTitle')}
            </Typography>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('pages.manager.qualityMetrics.agent.inputDescription')}
          </Typography>

          <Paper
            variant="outlined"
            sx={{ p: 2.5, mb: 3, bgcolor: 'background.paper' }}
          >
            <SelectField
              label={t('pages.manager.qualityMetrics.agent.inputLabel')}
              value={agentId}
              onChange={(e: SelectChangeEvent<unknown>) =>
                setAgentId(String(e.target.value ?? ''))
              }
              options={[
                {
                  value: '',
                  label: t(
                    'pages.manager.qualityMetrics.agent.inputPlaceholder'
                  ),
                },
                ...directReportOptions,
              ]}
              disabled={
                isLoadingDirectReports || directReportOptions.length === 0
              }
              fullWidth
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleAgentSearch}
                disabled={
                  !agentId.trim() || isSearching || isLoadingDirectReports
                }
              >
                {isSearching
                  ? t('pages.manager.qualityMetrics.agent.searching')
                  : t('pages.manager.qualityMetrics.agent.search')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setAgentId('');
                  setSearchMode(null);
                }}
              >
                {t('pages.manager.qualityMetrics.agent.clear')}
              </Button>
            </Box>
          </Paper>

          {!isSearching && agentId && (
            <Paper
              variant="outlined"
              sx={{ p: 2.5, bgcolor: 'background.paper' }}
            >
              {agentLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="50vh"
                >
                  <CircularProgress />
                </Box>
              ) : agentError || !agentData ? (
                <Box sx={{ p: 3 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, bgcolor: 'error.light' }}
                  >
                    <Typography color="error" variant="h6">
                      {t('pages.manager.qualityMetrics.agent.errorTitle')}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                      {agentError
                        ? t('pages.manager.qualityMetrics.agent.errorMessage')
                        : t('pages.manager.qualityMetrics.agent.noData')}
                    </Typography>
                  </Paper>
                </Box>
              ) : (
                <Box sx={{ p: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom>
                      {t('pages.manager.qualityMetrics.agent.resultTitle')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('pages.manager.qualityMetrics.agent.resultLabel')}:{' '}
                      {directReportOptions.find(
                        option => option.value === agentData.agentId
                      )?.label || agentData.agentId}
                    </Typography>
                  </Box>

                  {/* Date Range Filters */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      {t('pages.manager.qualityMetrics.agent.dateRangeFilter')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                      }}
                    >
                      <Box>
                        <TextField
                          type="date"
                          label={t(
                            'pages.manager.qualityMetrics.agent.startDate'
                          )}
                          value={startTime}
                          onChange={e =>
                            handleDateChange('start', e.target.value)
                          }
                          fullWidth
                          inputProps={{ max: endTime }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          type="date"
                          label={t(
                            'pages.manager.qualityMetrics.agent.endDate'
                          )}
                          value={endTime}
                          onChange={e =>
                            handleDateChange('end', e.target.value)
                          }
                          fullWidth
                          inputProps={{ min: startTime }}
                        />
                      </Box>
                    </Box>
                  </Paper>

                  {/* Summary Stats */}
                  <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: '1fr 1fr',
                          md: '1fr 1fr 1fr 1fr',
                        },
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t(
                            'pages.manager.qualityMetrics.agent.totalCallsAnalyzed'
                          )}
                        </Typography>
                        <Typography variant="h5">
                          {agentData.totalCalls}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t(
                            'pages.manager.qualityMetrics.agent.totalInterrupts'
                          )}
                        </Typography>
                        <Typography variant="h5">
                          {agentData.totalInterruptCount}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t(
                            'pages.manager.qualityMetrics.agent.avgNonTalkTime'
                          )}
                        </Typography>
                        <Typography variant="h5">
                          {Math.floor(agentData.averageNonTalkTimeSeconds / 60)}
                          m
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t('pages.manager.qualityMetrics.agent.period')}
                        </Typography>
                        <Typography variant="body2">
                          {new Date(agentData.periodStart).toLocaleDateString()}{' '}
                          - {new Date(agentData.periodEnd).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Charts */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 3,
                    }}
                  >
                    <SentimentChart
                      data={agentData.averageSentiment}
                      title={t(
                        'pages.manager.qualityMetrics.agent.averageSentiment'
                      )}
                    />
                    <TalkTimeChart
                      data={agentData.averageTalkTimeBreakdown}
                      title={t(
                        'pages.manager.qualityMetrics.agent.averageTalkTime'
                      )}
                    />
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Paper>
      )}

      {/* Call Search Form & Results */}
      {searchMode === 'call' && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 4,
            bgcolor: 'action.hover',
            border: '2px solid',
            borderColor: 'primary.main',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              {t('pages.manager.qualityMetrics.call.searchTitle')}
            </Typography>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('pages.manager.qualityMetrics.call.inputDescription')}
          </Typography>

          <Paper
            variant="outlined"
            sx={{ p: 2.5, mb: 3, bgcolor: 'background.paper' }}
          >
            <SelectField
              label={t('pages.manager.qualityMetrics.call.selectContact')}
              value={callId}
              onChange={(e: SelectChangeEvent<unknown>) =>
                setCallId(String(e.target.value ?? ''))
              }
              options={[
                {
                  value: '',
                  label: isLoadingCallOptions
                    ? t('pages.manager.qualityMetrics.call.loadingContacts')
                    : teamAgentIdsForCallHistory.length === 0
                      ? t('pages.manager.qualityMetrics.call.noContactsFound')
                      : t('pages.manager.qualityMetrics.call.selectContact'),
                },
                ...callOptions,
              ]}
              disabled={
                isLoadingCallOptions ||
                teamAgentIdsForCallHistory.length === 0 ||
                (!isLoadingCallOptions && callOptions.length === 0)
              }
              fullWidth
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleCallSearch}
                disabled={!callId.trim() || isSearching}
              >
                {isSearching
                  ? t('pages.manager.qualityMetrics.call.searching')
                  : t('pages.manager.qualityMetrics.call.search')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setCallId('');
                  setSearchMode(null);
                }}
              >
                {t('pages.manager.qualityMetrics.call.clear')}
              </Button>
            </Box>
          </Paper>

          {!isSearching && callId && (
            <Paper
              variant="outlined"
              sx={{ p: 2.5, bgcolor: 'background.paper' }}
            >
              {callLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="50vh"
                >
                  <CircularProgress />
                </Box>
              ) : callError || !callData ? (
                <Box sx={{ p: 3 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, bgcolor: 'error.light' }}
                  >
                    <Typography color="error" variant="h6">
                      {t('pages.manager.qualityMetrics.call.errorTitle')}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                      {callError
                        ? t('pages.manager.qualityMetrics.call.errorMessage')
                        : t('pages.manager.qualityMetrics.call.noData')}
                    </Typography>
                  </Paper>
                </Box>
              ) : (
                <Box sx={{ p: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom>
                      {t('pages.manager.qualityMetrics.call.resultTitle')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('pages.manager.qualityMetrics.call.resultLabel')}:{' '}
                      {callId}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <CallQualityCard data={callData} />
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 3,
                      mb: 3,
                    }}
                  >
                    <SentimentChart data={callData.averageSentiment} />
                    <TalkTimeChart data={callData.talkTimeBreakdown} />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <SentimentTrend
                      progression={callData.sentimentTrend.progression}
                    />
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Paper>
      )}

      {/* Info Section */}
      <Paper variant="outlined" sx={{ p: 3, bgcolor: 'action.hover' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {t('pages.manager.qualityMetrics.about.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('pages.manager.qualityMetrics.about.description')}
        </Typography>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {t('pages.manager.qualityMetrics.about.metricsAvailable')}
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('pages.manager.qualityMetrics.about.sentimentAnalysis')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('pages.manager.qualityMetrics.about.sentimentTrend')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('pages.manager.qualityMetrics.about.talkTime')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('pages.manager.qualityMetrics.about.silenceAnalysis')}
          </Typography>
          <Typography variant="body2">
            {t('pages.manager.qualityMetrics.about.interruptRate')}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
